import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CardMedia,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@material-ui/core";
import ConfidenceThresholdAnalyzer from "./ConfidenceThresholdAnalyzer";
import { makeStyles } from "@material-ui/core/styles";
import { dimensions } from "../constants";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    overflow: "auto",
  },
  card: {
    width: dimensions.width,
    height: dimensions.height / 1.5,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    marginBottom: theme.spacing(3),
    boxShadow: "0 3px 5px 2px rgba(128, 128, 128, .3)",
  },
  media: {
    height: dimensions.height / 1.8,
    width: "60%", // Adjust based on your needs
    margin: "auto", // Center the video
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: theme.spacing(2),
  },
  helpCard: {
    width: dimensions.width,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: "0 3px 5px 2px rgba(128, 128, 128, .3)",
  },
  helpTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  helpText: {
    fontSize: 16,
    marginBottom: theme.spacing(1),
  },
}));

const videos = [
  {
    title: "AudioProfile Demo",
    url: "https://www.youtube.com/embed/s72v-ZwFS8k?si=shiRMvEqc8b0RztW",
  },
  {
    title: "AudioProfile Surface Demo",
    url: "https://www.youtube.com/embed/xkrHWzpp6Gw?si=y_89r-ZodqU5elAp",
  },
  {
    title: "Running AudioGene Demo",
    url: "https://www.youtube.com/embed/IMwICgSoAFU?si=nV716dKgvxd1RUiQ",
  },
  {
    title: "Running AGTD Demo",
    url: "https://www.youtube.com/embed/edNFLOwx0Dc?si=KqxUnqxgsDXORjr7",
  },
];

export default function YoutubeDemo() {
  const classes = useStyles();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  return (
    <Box className={classes.root}>
      {videos.map((video, index) => (
        <Card key={index} className={classes.card}>
          <CardContent>
            <Typography variant="h5" className={classes.title}>
              {video.title}
            </Typography>
            <CardMedia
              component="iframe"
              className={classes.media}
              src={video.url}
              title={video.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </CardContent>
        </Card>
      ))}
      <Card id="confidence-score" className={classes.helpCard}>
        <CardContent>
          <Typography variant="h6" className={classes.helpTitle}>
            Confidence Score
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            The confidence score is a score between 0 and 1, calculated by summing the probabilities of
            the top 3 predicted genes. This provides a measure of how confident
            the model is in its prediction.
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            A higher confidence score indicates that the model's top predictions
            account for a larger share of the overall probability distribution,
            suggesting a more decisive classification.
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            Because AG9 and AG4 have different probability distributions, each
            model uses its own set of thresholds:
          </Typography>
          <TableContainer component={Paper} style={{ marginTop: 8, marginBottom: 8 }}>
            <Table size="small">
              <TableHead>
                <TableRow style={{ backgroundColor: "#e0e0e0" }}>
                  <TableCell><strong>Level</strong></TableCell>
                  <TableCell><strong>AG9 Range</strong></TableCell>
                  <TableCell><strong>AG4 Range</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { level: "Extremely Low", ag9: "0 – 0.6",     ag4: "0 – 0.6",     color: "#ffcccc" },
                  { level: "Low",           ag9: "0.6 – 0.75",  ag4: "0.6 – 0.72",  color: "#ffe0b2" },
                  { level: "Medium",        ag9: "0.75 – 0.87", ag4: "0.72 – 0.86", color: "#fff9c4" },
                  { level: "High",          ag9: "0.87 – 0.93", ag4: "0.86 – 0.91", color: "#c8e6c9" },
                  { level: "Extremely High",ag9: "0.93 – 1",    ag4: "0.91 – 1",    color: "#a5d6a7" },
                ].map((row) => (
                  <TableRow key={row.level} style={{ backgroundColor: row.color }}>
                    <TableCell><strong>{row.level}</strong></TableCell>
                    <TableCell>{row.ag9}</TableCell>
                    <TableCell>{row.ag4}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <Card id="threshold-analyzer" className={classes.helpCard}>
        <CardContent>
          <Typography variant="h6" className={classes.helpTitle}>
            Custom Threshold Analysis
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            Adjust the confidence score cutoffs and select a model to explore
            how your prediction samples distribute across confidence levels.
          </Typography>
          <ConfidenceThresholdAnalyzer />
        </CardContent>
      </Card>
      <Card className={classes.helpCard}>
        <CardContent>
          <Typography variant="h6" className={classes.helpTitle}>
            Tips and Tricks
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            - Double-click a gene in the legend of a plot to isolate that gene.
            Double-click it again to select all genes.
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            - Hover over a plot to highlight the cluster. Be careful not to
            accidentally switch clusters by hovering over another bar.
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            - Use the "Reset View" button to return to the default camera
            position in 3D plots.
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            - Toggle the "Show Percentage" switch to view gene distribution as
            percentages instead of counts.
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            - Use the "Visualization Method" dropdown to switch between
            different visualization modes.
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            - Double-click to rescale the y-axis to local min and max in the
            AudioProfile plots.
          </Typography>
          <Typography variant="body1" className={classes.helpText}>
            - To select a new cluster to be highlighted from the bar chart,
            click on a bar in the bar chart. This will then highlight the
            clicked cluster in the 3D plot.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
